import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, Star, Calendar, ShieldCheck, MessageCircle } from 'lucide-react';
import { usersAPI, reviewsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import ListingCard from '../components/ListingCard';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString('en-CA', { year: 'numeric', month: 'long' });
}

export default function ProfilePage() {
  const { userId } = useParams();
  const { user: currentUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);

  useEffect(() => {
    Promise.all([
      usersAPI.getProfile(userId).then(r => setProfile(r.data)),
      reviewsAPI.getForUser(userId).then(r => setReviews(r.data))
    ]).catch(() => {}).finally(() => setLoading(false));
  }, [userId]);

  const handleSubmitReview = async () => {
    if (!currentUser) return;
    setSubmittingReview(true);
    try {
      await reviewsAPI.create({ reviewed_user_id: userId, rating: reviewRating, comment: reviewComment });
      toast.success('Review submitted!');
      const { data } = await reviewsAPI.getForUser(userId);
      setReviews(data);
      setReviewComment('');
      setReviewRating(5);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!profile) return (
    <div className="min-h-screen flex flex-col items-center justify-center">
      <p className="text-lg text-zinc-500">Profile not found</p>
    </div>
  );

  const isOwnProfile = currentUser?.user_id === userId;

  return (
    <div className="min-h-screen bg-white" data-testid="profile-page">
      <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-8 sm:py-12">
        {/* Profile Header */}
        <div className="flex flex-col sm:flex-row gap-6 items-start mb-12" data-testid="profile-header">
          <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-zinc-200 flex items-center justify-center text-3xl font-bold text-zinc-400 flex-shrink-0 overflow-hidden">
            {profile.avatar ? <img src={profile.avatar} alt="" className="w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover" /> : profile.name?.charAt(0)}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900" data-testid="profile-name">{profile.name}</h1>
              {profile.verified && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 gap-1" data-testid="verified-badge">
                  <ShieldCheck className="w-3 h-3" /> Verified
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-4 text-sm text-zinc-500 mb-3">
              <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {profile.city}</span>
              <span className="flex items-center gap-1"><Star className="w-4 h-4 text-amber-500 fill-amber-500" /> {profile.rating?.toFixed(1)} ({profile.reviews_count} reviews)</span>
              <span className="flex items-center gap-1"><Calendar className="w-4 h-4" /> Joined {formatDate(profile.created_at)}</span>
            </div>
            {profile.bio && <p className="text-sm text-zinc-600">{profile.bio}</p>}

            {isOwnProfile && (
              <Link to="/settings" className="mt-3 inline-flex items-center px-4 py-2 rounded-full border border-zinc-200 text-sm font-medium text-zinc-700 hover:bg-zinc-50" data-testid="edit-profile-btn">
                Edit Profile
              </Link>
            )}
          </div>
        </div>

        {/* Listings */}
        <div className="mb-12">
          <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-zinc-900 mb-6" data-testid="profile-listings-heading">
            Listings ({profile.listings?.length || 0})
          </h2>
          {profile.listings?.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {profile.listings.map(l => <ListingCard key={l.listing_id} listing={l} />)}
            </div>
          ) : (
            <p className="text-sm text-zinc-400">No active listings</p>
          )}
        </div>

        {/* Reviews */}
        <div>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-zinc-900" data-testid="profile-reviews-heading">
              Reviews ({reviews.length})
            </h2>
            {currentUser && !isOwnProfile && (
              <Dialog>
                <DialogTrigger asChild>
                  <button className="bg-[#002FA7] text-white hover:bg-[#002585] rounded-full px-4 py-2 text-sm font-bold" data-testid="write-review-btn">Write a Review</button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Review {profile.name}</DialogTitle></DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2 block">Rating</label>
                      <div className="flex gap-1">
                        {[1, 2, 3, 4, 5].map(s => (
                          <button key={s} onClick={() => setReviewRating(s)} data-testid={`review-star-${s}`}>
                            <Star className={`w-6 h-6 ${s <= reviewRating ? 'text-amber-500 fill-amber-500' : 'text-zinc-300'}`} />
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2 block">Comment</label>
                      <textarea value={reviewComment} onChange={e => setReviewComment(e.target.value)} rows={3}
                        className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#002FA7]"
                        data-testid="review-comment-input"
                      />
                    </div>
                    <button onClick={handleSubmitReview} disabled={submittingReview || !reviewComment.trim()}
                      className="w-full bg-[#002FA7] text-white rounded-full py-2.5 font-bold text-sm disabled:opacity-50"
                      data-testid="submit-review-btn"
                    >{submittingReview ? 'Submitting...' : 'Submit Review'}</button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>

          <div className="space-y-4">
            {reviews.length > 0 ? reviews.map(r => (
              <div key={r.review_id} className="border border-zinc-200 rounded-lg p-4" data-testid={`review-${r.review_id}`}>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 rounded-full bg-zinc-200 flex items-center justify-center text-xs font-bold text-zinc-500">
                    {r.reviewer_avatar ? <img src={r.reviewer_avatar} alt="" className="w-full h-full rounded-full object-cover" /> : r.reviewer_name?.charAt(0)}
                  </div>
                  <div>
                    <p className="font-semibold text-sm text-zinc-900">{r.reviewer_name}</p>
                    <div className="flex items-center gap-1">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className={`w-3 h-3 ${i < r.rating ? 'text-amber-500 fill-amber-500' : 'text-zinc-300'}`} />
                      ))}
                      <span className="text-xs text-zinc-400 ml-2">{new Date(r.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
                <p className="text-sm text-zinc-600">{r.comment}</p>
              </div>
            )) : (
              <p className="text-sm text-zinc-400">No reviews yet</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
